import React, { useState } from 'react'
import Editor from '@monaco-editor/react'

const LANGUAGE_LABELS = { python: 'Python', javascript: 'JavaScript' }

export function McqQuestion({ q, index, value, onChange }) {
  return (
    <div className="std-card">
      <h3>{index}. {q.prompt}</h3>
      {q.code && <pre className="std-pre">{q.code}</pre>}
      {q.options.map((opt, i) => (
        <label key={i} className={`std-option ${value === i ? 'std-option-selected' : ''}`}>
          <input type="radio" name={q.id} checked={value === i} onChange={() => onChange(i)} />
          <span style={{ fontFamily: q.code && /^[\d\s-]+$/.test(opt) ? 'monospace' : undefined }}>{opt}</span>
        </label>
      ))}
    </div>
  )
}

// Used for both debugging (hypothesis-first) and write-a-program questions.
export function CodeQuestion({ q, language, value, onChange, hypothesis, onHypothesis, onRun }) {
  const isDebug = q.type === 'debug'
  const locked = isDebug && !hypothesis
  const [draft, setDraft] = useState('')
  const [savingHypothesis, setSavingHypothesis] = useState(false)
  const [hypothesisError, setHypothesisError] = useState('')
  const [customInput, setCustomInput] = useState('')
  const [useCustom, setUseCustom] = useState(false)
  const [running, setRunning] = useState(false)
  const [run, setRun] = useState(null)

  const code = value ?? q.starter
  const sample = isDebug ? q.sample : q.samples[0]

  const lockHypothesis = async () => {
    setSavingHypothesis(true)
    setHypothesisError('')
    try {
      await onHypothesis(draft)
    } catch (err) {
      setHypothesisError(err.message)
    } finally {
      setSavingHypothesis(false)
    }
  }

  const doRun = async () => {
    setRunning(true)
    setRun(null)
    try {
      setRun(await onRun(code, useCustom ? customInput : undefined))
    } catch (err) {
      setRun({ error: err.message })
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className="std-card">
      <h3>{q.id}. {q.title}</h3>
      {isDebug && <div className="std-alert std-alert-bad"><strong>Bug report:</strong> {q.report}</div>}
      <p style={{ margin: '8px 0' }}>{isDebug ? q.spec : q.statement}</p>
      <p className="std-muted std-small" style={{ margin: '4px 0' }}><strong>Input:</strong> {q.inputFormat}</p>
      <div className="std-grid-2">
        <div><div className="std-small std-muted">Sample input</div><pre className="std-pre std-pre-light">{sample.input}</pre></div>
        <div><div className="std-small std-muted">Expected output</div><pre className="std-pre std-pre-light">{sample.expected}</pre></div>
      </div>

      {isDebug && (
        <div className="std-card" style={{ background: hypothesis ? 'var(--std-ok-soft)' : 'var(--std-accent-soft)', borderColor: 'transparent' }}>
          <h3 style={{ marginBottom: 4 }}>Your hypothesis</h3>
          {hypothesis ? (
            <>
              <p style={{ whiteSpace: 'pre-wrap', margin: '4px 0' }}>{hypothesis.text}</p>
              <div className="std-small std-muted">Locked at {new Date(hypothesis.at).toLocaleTimeString()}. The editor is now unlocked.</div>
            </>
          ) : (
            <>
              <p className="std-small std-muted" style={{ margin: '0 0 8px' }}>
                Before touching the code: what do you think causes the bug, and why? Point at the line or condition if you can.
                Once locked, this cannot be changed.
              </p>
              <textarea value={draft} onChange={e => setDraft(e.target.value)} placeholder="I think the bug is ... because ..." />
              {hypothesisError && <div className="std-alert std-alert-bad">{hypothesisError}</div>}
              <div style={{ marginTop: 8, display: 'flex', gap: 10, alignItems: 'center' }}>
                <button className="std-btn" disabled={draft.trim().length < 20 || savingHypothesis} onClick={lockHypothesis}>
                  {savingHypothesis ? 'Saving…' : 'Lock in hypothesis'}
                </button>
                <span className="std-small std-muted">{draft.trim().length < 20 ? `${20 - draft.trim().length} more characters` : 'Ready'}</span>
              </div>
            </>
          )}
        </div>
      )}

      <div className="std-small std-muted" style={{ margin: '12px 0 6px' }}>
        {LANGUAGE_LABELS[language]} {locked && '— locked until you write your hypothesis'}
      </div>
      <div className={`std-editor ${locked ? 'std-editor-locked' : ''}`}>
        <Editor
          height="360px"
          language={language}
          value={code}
          onChange={v => onChange(v ?? '')}
          options={{ readOnly: locked, minimap: { enabled: false }, fontSize: 14, scrollBeyondLastLine: false, tabSize: language === 'python' ? 4 : 2 }}
        />
      </div>

      <div style={{ marginTop: 10 }}>
        <label className="std-small" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={useCustom} onChange={e => setUseCustom(e.target.checked)} disabled={locked} />
          Run with my own input instead of the sample
        </label>
        {useCustom && (
          <textarea style={{ minHeight: 70, fontFamily: 'monospace', marginTop: 6 }} value={customInput}
            onChange={e => setCustomInput(e.target.value)} placeholder={'1\n...'} />
        )}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8 }}>
          <button className="std-btn std-btn-ghost" onClick={doRun} disabled={locked || running}>
            {running ? (language === 'python' ? 'Running… (first run loads Python)' : 'Running…') : '▶ Run'}
          </button>
          <span className="std-small std-muted">Runs in your browser · hidden tests run when you submit the diagnostic</span>
        </div>
        {run && (
          <div>
            {run.error && <div className="std-alert std-alert-bad">{run.error}</div>}
            {!run.error && run.matches !== null && (
              <div className={`std-alert ${run.matches ? 'std-alert-ok' : 'std-alert-warn'}`}>
                {run.matches ? 'Output matches the sample.' : 'Output does not match the sample.'}
              </div>
            )}
            {(run.output || !run.error) && <pre className="std-pre std-output">{run.output || '(no output)'}</pre>}
          </div>
        )}
      </div>
    </div>
  )
}

const emptyRow = () => ({ age: '', day: '', expected: '', why: '' })

export function TestsQuestion({ q, value, onChange }) {
  const rows = value && value.length ? value : [emptyRow()]
  const update = (i, key, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [key]: v } : r)))
  const add = () => rows.length < q.maxRows && onChange([...rows, emptyRow()])
  const remove = (i) => onChange(rows.filter((_, j) => j !== i))

  return (
    <div className="std-card">
      <h3>{q.id}. Write tests for <code>{q.title}</code></h3>
      <ul style={{ paddingLeft: 20, margin: '8px 0 14px' }}>
        {q.spec.map((line, i) => <li key={i} style={{ margin: '4px 0' }}>{line}</li>)}
      </ul>
      <div className="std-alert std-alert-info std-small">
        Each row is one test: the inputs and the output the spec requires. Write INVALID when the spec says so.
        Your tests are run against {q.mutantCount} subtly broken implementations — the more they catch, the better.
      </div>
      <div className="std-table-wrap">
        <table className="std-table">
          <thead>
            <tr><th style={{ width: 40 }}>#</th><th style={{ width: 110 }}>age</th><th style={{ width: 130 }}>day</th><th style={{ width: 130 }}>expected</th><th>what it checks</th><th style={{ width: 40 }}></th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="std-muted">{i + 1}</td>
                <td><input type="text" value={r.age} placeholder="12" onChange={e => update(i, 'age', e.target.value)} /></td>
                <td><input type="text" value={r.day} placeholder="TUE" onChange={e => update(i, 'day', e.target.value)} /></td>
                <td><input type="text" value={r.expected} placeholder="50" onChange={e => update(i, 'expected', e.target.value)} /></td>
                <td><input type="text" value={r.why} placeholder="upper boundary of child price" onChange={e => update(i, 'why', e.target.value)} /></td>
                <td><button className="std-btn std-btn-ghost std-btn-sm" onClick={() => remove(i)} aria-label="Remove row">✕</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button className="std-btn std-btn-ghost std-btn-sm" style={{ marginTop: 10 }} onClick={add} disabled={rows.length >= q.maxRows}>
        + Add test ({rows.length}/{q.maxRows})
      </button>
    </div>
  )
}

export function ReviewQuestion({ q, value, onChange }) {
  const v = value || { problems: '', refactor: '' }
  return (
    <div className="std-card">
      <h3>{q.id}. {q.title}</h3>
      <p className="std-muted" style={{ margin: '4px 0' }}>{q.context}</p>
      <pre className="std-pre">{q.code}</pre>
      <label style={{ display: 'block', fontWeight: 600, margin: '12px 0 6px' }}>{q.prompts.problems}</label>
      <textarea style={{ minHeight: 160 }} value={v.problems} onChange={e => onChange({ ...v, problems: e.target.value })} />
      <label style={{ display: 'block', fontWeight: 600, margin: '12px 0 6px' }}>{q.prompts.refactor}</label>
      <textarea style={{ minHeight: 200, fontFamily: 'monospace' }} value={v.refactor} onChange={e => onChange({ ...v, refactor: e.target.value })} />
    </div>
  )
}

export function isAnswered(q, value, hypothesis) {
  if (value === undefined || value === null) return false
  switch (q.type) {
    case 'mcq': return Number.isInteger(value)
    case 'debug': return !!hypothesis && value !== q.starter
    case 'code': return value !== q.starter && value.trim() !== ''
    case 'tests': return value.some(r => r.age?.trim() && r.expected?.trim())
    case 'review': return !!(value.problems?.trim() || value.refactor?.trim())
    default: return false
  }
}
